import React, { useState, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { crearPuerta } from '@/features/puerta/puertaThunks';
import { agregarPuerta, setLoadingPuertas, puertaFailure } from '@/features/puerta/puertaSlice';
import { DateTime } from 'luxon';
import { parseUTCDate, DEFAULT_TIMEZONE } from '@/utils/dateHelpers';

const QUICK_JUMPS = [60, 120, 180, 240, 360, 480];

function parseUTCValue(val, tz) {
  if (!val) return null;
  let dt = DateTime.fromSQL(val, { zone: 'utc' });
  if (dt.isValid) return dt.setZone(tz);
  dt = DateTime.fromISO(val, { zone: 'utc' });
  return dt.isValid ? dt.setZone(tz) : null;
}

function MinutesOffsetStepper({ label, description, value, onChange, eventStart, timezone, minOffset = 5 }) {
  const tz = timezone || DEFAULT_TIMEZONE;

  const startDT = useMemo(() => parseUTCDate(eventStart, tz), [eventStart, tz]);
  const valueDT = useMemo(() => parseUTCValue(value, tz), [value, tz]);

  const offsetMinutes = useMemo(() => {
    if (!valueDT || !startDT) return null;
    return Math.round(valueDT.diff(startDT, 'minutes').minutes);
  }, [valueDT, startDT]);

  const computedDisplay = useMemo(() => {
    if (!valueDT) return null;
    return valueDT.toFormat('dd/MM/yyyy HH:mm');
  }, [valueDT]);

  const formatOffset = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `+${m}min`;
    if (m === 0) return `+${h}h`;
    return `+${h}h ${m}min`;
  };

  const handleActivate = () => {
    if (!startDT) return;
    const activationOffset = Math.max(120, minOffset);
    const utcDT = startDT.plus({ minutes: activationOffset }).toUTC();
    onChange(utcDT.toFormat("yyyy-MM-dd'T'HH:mm"));
  };

  const handleStep = (delta) => {
    if (offsetMinutes === null || !startDT) return;
    const newOffset = offsetMinutes + delta;
    if (newOffset < minOffset) return;
    const utcDT = startDT.plus({ minutes: newOffset }).toUTC();
    onChange(utcDT.toFormat("yyyy-MM-dd'T'HH:mm"));
  };

  const holdRef = useRef(null);

  const startHold = (direction) => {
    if (offsetMinutes === null || !startDT) return;
    const startTime = Date.now();
    let acc = offsetMinutes;
    holdRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const step = elapsed > 3000 ? 60 : elapsed > 1500 ? 15 : 5;
      acc += direction * step;
      if (acc < minOffset) { acc = minOffset; return; }
      onChange(startDT.plus({ minutes: acc }).toUTC().toFormat("yyyy-MM-dd'T'HH:mm"));
    }, 150);
  };

  const stopHold = () => {
    clearInterval(holdRef.current);
    holdRef.current = null;
  };

  return (
    <div>
      <label className="block text-sm font-medium text-white mb-2">
        {label} <span className="text-red-500">*</span>
      </label>
      {description && <p className="text-gray-400 text-xs mb-2">{description}</p>}
      {offsetMinutes === null ? (
        <div className="flex items-center justify-between bg-aw-bluedark border border-gray-600 rounded-md px-3 py-2">
          <span className="text-gray-500 text-sm italic">Sin hora límite configurada</span>
          <button
            type="button"
            onClick={handleActivate}
            disabled={!startDT}
            className="text-aw-green text-xs font-semibold hover:underline disabled:opacity-40"
          >
            Configurar
          </button>
        </div>
      ) : (
        <div className="bg-aw-bluedark border border-gray-600 rounded-md px-3 py-2 space-y-2">
          {/* Quick jump chips */}
          <div className="flex gap-1 flex-wrap justify-center">
            {QUICK_JUMPS.filter((m) => m >= minOffset).map((mins) => {
              const isActive = offsetMinutes === mins;
              return (
                <button
                  key={mins}
                  type="button"
                  onClick={() => {
                    if (!startDT) return;
                    onChange(startDT.plus({ minutes: mins }).toUTC().toFormat("yyyy-MM-dd'T'HH:mm"));
                  }}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-aw-green text-aw-bluedark'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  +{mins / 60}h
                </button>
              );
            })}
          </div>

          {/* +/- stepper con long press */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleStep(-5)}
              onMouseDown={() => startHold(-1)}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={() => startHold(-1)}
              onTouchEnd={stopHold}
              disabled={offsetMinutes - 5 < minOffset}
              className="w-7 h-7 rounded bg-gray-700 text-white text-sm font-bold flex items-center justify-center disabled:opacity-40 hover:bg-gray-600 select-none"
            >
              −
            </button>
            <span className="text-white text-sm font-semibold flex-1 text-center">
              {formatOffset(offsetMinutes)} desde el inicio
            </span>
            <button
              type="button"
              onClick={() => handleStep(5)}
              onMouseDown={() => startHold(1)}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={() => startHold(1)}
              onTouchEnd={stopHold}
              className="w-7 h-7 rounded bg-gray-700 text-white text-sm font-bold flex items-center justify-center hover:bg-gray-600 select-none"
            >
              +
            </button>
          </div>
          {computedDisplay && (
            <p className="text-aw-green/80 text-xs text-center">{computedDisplay}</p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-red-400 text-xs hover:underline"
            >
              Quitar hora límite
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FormCrearPuerta({ onClose, uidEvento, uidEmpresa, eventStart, timezone }) {
  const dispatch = useDispatch();

  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    encargado: '',
    validateTicketsFinishTime: '',
  });

  // Estados de UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar errores al escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'El nombre de la puerta es obligatorio';
    }

    if (!formData.encargado.trim()) {
      errors.encargado = 'El nombre del encargado es obligatorio';
    }

    if (!formData.validateTicketsFinishTime) {
      errors.validateTicketsFinishTime = 'Debe configurar la hora límite de validación';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    dispatch(setLoadingPuertas(true));

    try {
      const result = await dispatch(crearPuerta({
        puertaData: {
          name: formData.name.trim(),
          idEvento: uidEvento,
          encargado: formData.encargado.trim(),
          validateTicketsFinishTime: formData.validateTicketsFinishTime,
          uidEmpresa,
        }
      })).unwrap();

      // Agregar la puerta al estado
      if (result.puerta) {
        dispatch(agregarPuerta(result.puerta));
      }

      // Mostrar mensaje de éxito
      setSuccessMessage('Puerta creada exitosamente');

      // Cerrar modal después de 1.5 segundos
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);

    } catch (error) {
      console.error('Error al crear puerta:', error);
      dispatch(puertaFailure(error));
      setFormErrors({ submit: typeof error === 'string' ? error : 'Error al crear la puerta' });
    } finally {
      setIsSubmitting(false);
      dispatch(setLoadingPuertas(false));
    }
  };

  // Verificar si el formulario es válido
  const isFormValid = formData.name.trim() &&
                     formData.encargado.trim() &&
                     formData.validateTicketsFinishTime;

  return (
    <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Crear Nueva Puerta Validadora</h2>
        <p className="text-white text-sm">Complete los datos para crear un dispositivo validador de tickets</p>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-aw-green bg-opacity-20 border border-aw-green rounded-md">
          <p className="text-aw-bluedark text-sm">{successMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Campo Nombre de la Puerta */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
            Nombre de la Puerta <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Ej: Puerta Principal, Entrada VIP, etc."
            className={`w-full px-3 py-2 bg-aw-bluedark text-white border rounded-md focus:outline-none focus:ring-2 focus:ring-aw-green ${
              formErrors.name ? 'border-red-500' : 'border-gray-600'
            }`}
            disabled={isSubmitting}
          />
          {formErrors.name && (
            <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
          )}
        </div>

        {/* Campo Encargado */}
        <div>
          <label htmlFor="encargado" className="block text-sm font-medium text-white mb-2">
            Encargado <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="encargado"
            name="encargado"
            value={formData.encargado}
            onChange={handleInputChange}
            placeholder="Nombre del responsable de esta puerta"
            className={`w-full px-3 py-2 bg-aw-bluedark text-white border rounded-md focus:outline-none focus:ring-2 focus:ring-aw-green ${
              formErrors.encargado ? 'border-red-500' : 'border-gray-600'
            }`}
            disabled={isSubmitting}
          />
          {formErrors.encargado && (
            <p className="mt-1 text-sm text-red-500">{formErrors.encargado}</p>
          )}
        </div>

        {/* Campo Fecha y Hora Límite de Validación */}
        <div>
          <MinutesOffsetStepper
            label="Fecha y Hora Límite de Validación"
            description="Tiempo máximo hasta el cual la puerta podrá validar tickets desde el inicio del evento."
            value={formData.validateTicketsFinishTime}
            onChange={(val) => {
              setFormData(prev => ({ ...prev, validateTicketsFinishTime: val || '' }));
              if (formErrors.validateTicketsFinishTime) {
                setFormErrors(prev => ({ ...prev, validateTicketsFinishTime: '' }));
              }
            }}
            eventStart={eventStart}
            timezone={timezone}
            minOffset={5}
          />
          {formErrors.validateTicketsFinishTime && (
            <p className="mt-1 text-sm text-red-500">{formErrors.validateTicketsFinishTime}</p>
          )}
        </div>

        {/* Error de submit */}
        {formErrors.submit && (
          <div className="p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded-md">
            <p className="text-white text-sm">{formErrors.submit}</p>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className={`px-6 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
              isFormValid && !isSubmitting
                ? 'bg-aw-green text-aw-bluedark hover:scale-95 transition-transform'
                : 'bg-gray-600 text-white cursor-not-allowed'
            }`}
          >
            {isSubmitting && (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSubmitting ? 'Creando...' : 'Crear Puerta'}
          </button>
        </div>
      </form>
    </div>
  );
}
